import * as React from "react";
import { Box, Alert, Spinner } from "@amzn/awsui-components-react";
import { ContentEvaluationModel } from "../../model/content-evaluation-model";
import { ContentEvaluationResults } from "./ContentEvaluationResults";
import { Container } from "@amzn/ask-legal-domain";
import { FlashbarProps } from "@amzn/awsui-components-react";

interface ContainerEvaluationListProps {
    selectedContainerIds: string[];
    containerMetadata: {[key: string]: Container.Data};
    containerEvaluationState: ContentEvaluationModel.ContainerEvaluationState;
    onScrolledToContainer: (containerId: string) => void;
    onFlashbarUpdated?: (flashbarProps: FlashbarProps) => void;
}

export const ContainerEvaluationList: React.FC<ContainerEvaluationListProps> = ({
    selectedContainerIds,
    containerMetadata,
    containerEvaluationState,
    onScrolledToContainer,
    onFlashbarUpdated
}) => {
    return (
        <Box>
            <h3 style={{
                color: "#374151",
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px"
            }}>
                Evaluation Results by Container
            </h3>

            {/* Container Names List */}
            <div style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "24px"
            }}>
                <h4 style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    margin: "0 0 12px 0"
                }}>
                    Containers
                </h4>
                <ul style={{
                    listStyle: "none",
                    padding: "0",
                    margin: "0"
                }}>
                    {selectedContainerIds.map(containerId => {
                        const containerName = containerMetadata?.[containerId]?.title || containerId;
                        const containerState = containerEvaluationState[containerId];
                        const isLoading = containerState?.loading || false;
                        const error = containerState?.error;
                        const result = containerState?.result;

                        let statusIcon = "⏳";
                        let statusText = "Loading...";
                        let statusColor = "#6b7280";

                        if (error) {
                            statusIcon = "❌";
                            statusText = "Error";
                            statusColor = "#dc2626";
                        } else if (result) {
                            const findings = result.data.totalFindings;
                            statusIcon = findings > 0 ? "⚠️" : "✅";
                            statusText = `${findings} findings`;
                            statusColor = findings > 0 ? "#d97706" : "#059669";
                        }

                        return (
                            <li key={containerId} style={{ marginBottom: "8px" }}>
                                <button
                                    onClick={() => onScrolledToContainer(containerId)}
                                    disabled={isLoading}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "8px 12px",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: isLoading ? "default" : "pointer",
                                        textAlign: "left",
                                        transition: "all 0.2s ease",
                                        opacity: isLoading ? 0.6 : 1,
                                        width: "100%"
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isLoading) {
                                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isLoading) {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }
                                    }}
                                >
                                    <span style={{
                                        fontSize: "16px",
                                        color: "#374151",
                                        marginRight: "4px"
                                    }}>
                                        •
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#374151",
                                            wordBreak: "break-word"
                                        }}>
                                            {containerName}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: "12px",
                                        color: statusColor,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        flexShrink: 0
                                    }}>
                                        <span>{statusIcon}</span>
                                        <span>{statusText}</span>
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Results Display */}
            <div>
                <div id="container-results-list">
                    {selectedContainerIds.map(containerId => {
                        const containerName = containerMetadata?.[containerId]?.title || containerId;
                        const containerState = containerEvaluationState[containerId];
                        const isLoading = containerState?.loading || false;
                        const error = containerState?.error;
                        const result = containerState?.result;

                        return (
                            <div
                                key={containerId}
                                id={`container-result-${containerId}`}
                                style={{
                                    marginBottom: "32px",
                                    paddingBottom: "24px",
                                    borderBottom: "2px solid #e5e7eb"
                                }}
                            >
                                <h4 style={{
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#374151",
                                    marginBottom: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    {containerName}
                                </h4>

                                {isLoading && (
                                    <Box textAlign="center" padding={{ vertical: "l" }}>
                                        <Spinner size="normal" />
                                    </Box>
                                )}

                                {error && (
                                    <Alert
                                        type="error"
                                        header={`Evaluation failed for ${containerName}`}
                                    >
                                        {error}
                                    </Alert>
                                )}

                                {result && !isLoading && !error && (
                                    <ContentEvaluationResults
                                        results={result.data}
                                        onFlashbarUpdated={onFlashbarUpdated}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </Box>
    );
};
